import sentinelLogo from "@/assets/images/Sentinel_Horizontal_TM.png";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { formatCurrency } from "@/lib/utils/currency";
import {
    EnrollmentTier,
    getEnrollmentTierLabel,
    getRetailPrice,
} from "@/lib/utils/enrollment";
import { EnrollmentFormField } from "@/types/field";
import { Organization } from "@/types/organization";
import { addToast } from "@heroui/react";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import JSZip from "jszip";
import { useCallback, useState } from "react";

function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export default function useEnrollmentInvoice(
    fields: EnrollmentFormField[],
    enrollmentTier?: EnrollmentTier,
    enrollmentStartDate?: Date,
) {
    const { userIsSentinel } = useAuth();
    const { axiosRequest } = useRequest();
    const [isDownloading, setIsDownloading] = useState(false);
    const isNewEnrollment = enrollmentTier !== undefined;
    const canDownloadInvoice =
        userIsSentinel &&
        fields.length > 0 &&
        fields.every(
            (field) =>
                field.name.trim() &&
                field.grower.name?.trim() &&
                (isNewEnrollment
                    ? getRetailPrice(enrollmentTier) !== undefined
                    : field.active_enrollment),
        );

    const downloadInvoice = useCallback(async (includePricing = false) => {
        if (isDownloading || !canDownloadInvoice) return;

        setIsDownloading(true);
        try {
            const groups = new Map<
                number | null,
                { name?: string; fields: EnrollmentFormField[] }
            >();
            for (const field of fields) {
                const provider = field.grower.service_provider;
                const cspId = isNewEnrollment
                    ? (provider?.id ?? null)
                    : (field.active_enrollment?.enrolling_organization ?? null);
                const name =
                    provider?.id === cspId ? provider.name?.trim() : undefined;
                const group = groups.get(cspId);
                if (group) {
                    group.fields.push(field);
                    if (!group.name) group.name = name;
                } else {
                    groups.set(cspId, { name, fields: [field] });
                }
            }

            const needsNames = Array.from(groups).some(
                ([id, group]) => id !== null && !group.name,
            );
            const organizations = needsNames
                ? await axiosRequest<Organization[]>({
                      url: "organizations/service_providers/",
                      method: "get",
                  })
                : [];
            const image = await new Promise<HTMLImageElement>(
                (resolve, reject) => {
                    const logo = new Image();
                    logo.onload = () => resolve(logo);
                    logo.onerror = reject;
                    logo.src = sentinelLogo;
                },
            );
            const pricingSuffix = includePricing ? "_with_pricing" : "";
            const invoices = Array.from(groups, ([cspId, group]) => {
                const name =
                    group.name ||
                    organizations.find((org) => org.id === cspId)?.name.trim() ||
                    (cspId === null ? "CSP unavailable" : `CSP ${cspId}`);
                let totalRetailValue = 0;
                const rows = group.fields.map((field) => {
                    const enrollment = field.active_enrollment;
                    const acres = Number(
                        isNewEnrollment
                            ? field.imagery_boundary_acres
                            : enrollment?.acres,
                    );
                    if (!Number.isFinite(acres)) {
                        throw new Error("Invalid invoice amount");
                    }
                    const row = [
                        field.grower.name ?? "",
                        field.name,
                        getEnrollmentTierLabel(
                            enrollmentTier ?? enrollment?.enrollment_tier,
                        ) ?? "",
                        acres,
                    ];
                    if (includePricing) {
                        const rate = Number(
                            isNewEnrollment
                                ? getRetailPrice(enrollmentTier)
                                : enrollment?.retail_price_per_acre,
                        );
                        const retailValue = isNewEnrollment
                            ? acres * rate
                            : Number(enrollment?.enrolled_value);
                        if (![rate, retailValue].every(Number.isFinite)) {
                            throw new Error("Invalid invoice amount");
                        }
                        totalRetailValue += retailValue;
                        row.push(
                            formatCurrency(rate) ?? "",
                            formatCurrency(retailValue) ?? "",
                        );
                    } else {
                        const dateEnrolled = isNewEnrollment
                            ? undefined
                            : enrollment?.date_enrolled;
                        const startDate = isNewEnrollment
                            ? enrollmentStartDate
                            : enrollment?.start_date;
                        const endDate = isNewEnrollment
                            ? enrollmentStartDate
                                ? dayjs(enrollmentStartDate).add(365, "day")
                                : undefined
                            : enrollment?.end_date;
                        row.push(
                            dateEnrolled
                                ? dayjs(dateEnrolled).format("MM/DD/YYYY")
                                : "--",
                            startDate
                                ? dayjs(startDate).format("MM/DD/YYYY")
                                : "--",
                            endDate
                                ? dayjs(endDate).format("MM/DD/YYYY")
                                : "--",
                        );
                    }
                    return row;
                });

                const doc = new jsPDF();
                const pageWidth = doc.internal.pageSize.getWidth();
                doc.addImage(image, "PNG", 15, 10, 80, 20);
                doc.setFontSize(12);
                doc.text(dayjs().format("MM/DD/YYYY"), pageWidth - 15, 20, {
                    align: "right",
                });
                doc.setFontSize(16);
                doc.text("Enrollment Invoice", 15, 38);
                doc.setFontSize(10);
                const cspLines = doc.splitTextToSize(
                    `CSP: ${name}`,
                    pageWidth - 30,
                );
                doc.text(cspLines, 15, 46);
                autoTable(doc, {
                    head: [
                        [
                            "Grower",
                            "Field",
                            "Tier",
                            "Acres",
                            ...(includePricing
                                ? ["Retail / acre", "Retail value"]
                                : [
                                      "Date enrolled",
                                      "Enrollment start",
                                      "Enrollment end",
                                  ]),
                        ],
                    ],
                    body: rows,
                    foot: includePricing
                        ? [
                              [
                                  {
                                      content: "Total retail value",
                                      colSpan: 5,
                                      styles: { halign: "right" },
                                  },
                                  formatCurrency(totalRetailValue) ?? "",
                              ],
                          ]
                        : [],
                    showFoot: "lastPage",
                    rowPageBreak: "avoid",
                    startY: 50 + (cspLines.length - 1) * 5,
                    theme: "grid",
                    bodyStyles: {
                        fontSize: 8,
                        overflow: "linebreak",
                    },
                    headStyles: {
                        fontSize: 8,
                        fillColor: [0, 51, 102],
                        textColor: [255, 255, 255],
                        overflow: "linebreak",
                    },
                    footStyles: {
                        fontSize: 8,
                        fillColor: [255, 255, 255],
                        textColor: [0, 0, 0],
                        lineWidth: 0.1,
                        halign: "right",
                    },
                    columnStyles: includePricing
                        ? {
                              3: { halign: "right" },
                              4: { halign: "right" },
                              5: { halign: "right" },
                          }
                        : { 3: { halign: "right" } },
                });
                const filename = name
                    .replace(/[^\w.-]+/g, "_")
                    .replace(/_+/g, "_");
                return {
                    doc,
                    filename: `enrollment_invoice_${filename}_${cspId ?? "unavailable"}${pricingSuffix}.pdf`,
                };
            });

            if (invoices.length === 1) {
                downloadBlob(invoices[0].doc.output("blob"), invoices[0].filename);
            } else {
                const zip = new JSZip();
                for (const invoice of invoices) {
                    zip.file(invoice.filename, invoice.doc.output("arraybuffer"));
                }
                downloadBlob(
                    await zip.generateAsync({ type: "blob" }),
                    `enrollment_invoices${pricingSuffix}.zip`,
                );
            }
            addToast({
                title: "Invoice downloaded",
                timeout: 3000,
                color: "success",
            });
        } catch {
            addToast({
                title: "Failed to download invoice",
                timeout: 3000,
                color: "danger",
            });
        } finally {
            setIsDownloading(false);
        }
    }, [
        axiosRequest,
        canDownloadInvoice,
        enrollmentStartDate,
        enrollmentTier,
        fields,
        isDownloading,
        isNewEnrollment,
    ]);

    return { downloadInvoice, isDownloading, canDownloadInvoice };
}
